import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, Loader2, Mail, MoreVertical, Shield, Trash2, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { BOTTONE, GRIGIO, SCURO } from "./integrations/casella";
import { CAMPO_TESTO, Campo } from "./widget/campi";
import { useTranslation } from "../hooks/useTranslation";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RUOLI = {
  owner: { chiave: "owner", cls: "bg-[rgba(173,70,255,0.12)] text-[#A855F7]" },
  admin: { chiave: "admin", cls: "bg-[#F1F5F9] text-[#334155]" },
  member: { chiave: "member", cls: "text-[#475569]" },
};

const STATI = {
  active: { chiave: "statusActive", dot: "bg-[#19D163]", cls: "bg-[#19D163]/10 text-[#0F7A3D]" },
  pending: { chiave: "statusPending", dot: "bg-amber-500", cls: "bg-amber-50 text-[#92400E]" },
  suspended: { chiave: "statusSuspended", dot: "bg-[#94A3B8]", cls: "bg-[#F1F5F9] text-[#475569]" },
};

const TESTATA = "h-10 text-xs font-medium text-[#64748B]";

/** Le iniziali per il cerchietto: nome e cognome, o l'inizio dell'email. */
function iniziali(persona) {
  const nome = (persona?.name && persona.name !== persona.email ? persona.name : "").trim();
  if (nome) {
    const parti = nome.split(/\s+/);
    return ((parti[0]?.[0] || "") + (parti.length > 1 ? parti.at(-1)[0] : "")).toUpperCase();
  }
  return (persona?.email || "?").slice(0, 2).toUpperCase();
}

/**
 * Le persone dell'organizzazione: il titolare, i membri e gli inviti in
 * attesa, in una tabella. Le azioni dipendono dal proprio ruolo, come
 * sull'API: titolare e amministratori invitano e rimuovono, solo il
 * titolare cambia i ruoli. Nessuno rimuove se stesso o il titolare.
 * Si invita solo come amministratore (decisione di Gianluca, 23/10): i
 * "membri" di prima restano, e il titolare li puo' rendere amministratori.
 */
export default function MembersManagement({ tenantId }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { activeTenant } = useTenant();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [daRimuovere, setDaRimuovere] = useState(null);

  // Senza ruolo noto (es. accesso da amministratore di sistema) le azioni
  // restano visibili e decide l'API.
  const mioRuolo = activeTenant?.role;
  const gestisce = !mioRuolo || mioRuolo === "owner" || mioRuolo === "admin";
  const titolare = !mioRuolo || mioRuolo === "owner";
  const io = user?.id ? String(user.id) : null;

  const T = (chiave, opzioni) => t(`components.members.${chiave}`, opzioni);
  const giorno = (d) =>
    d ? new Date(d).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }) : null;

  const membri = useQuery({
    queryKey: ["members", tenantId],
    queryFn: async () => {
      const response = await api.get(`/tenants/${tenantId}/members`);
      return response.data;
    },
    enabled: !!tenantId,
  });

  const aggiorna = () => queryClient.invalidateQueries({ queryKey: ["members", tenantId] });
  // I messaggi dell'API sono in inglese: si traduce dal codice in `error`.
  const MOTIVI = {
    "Already member": "errorAlreadyMember",
    "Invite pending": "errorInvitePending",
    "Invalid email": "inviteDialog.invalidEmailDesc",
    "Invalid operation": "errorRemoveSelf",
    "Member not found": "errorNotFound",
    "Invalid role": "errorInvalidRole",
  };
  const motivo = (error) => {
    const codice = error.response?.data?.error;
    if (MOTIVI[codice]) return T(MOTIVI[codice]);
    if (error.response?.status === 403) return t("errors.api.forbidden");
    return T("errorGeneric");
  };
  const errore = (titolo) => (error) => {
    toast({ title: titolo, description: motivo(error), variant: "destructive" });
    // Chi e' cambiato nel frattempo (rimosso, gia' entrato): l'elenco si rilegge.
    if (error.response?.status === 404 || error.response?.status === 409) aggiorna();
  };

  const inviteMutation = useMutation({
    mutationFn: async ({ email, role }) => {
      const response = await api.post(`/tenants/${tenantId}/members/invite`, { email, role });
      return response.data;
    },
    onSuccess: () => {
      toast({
        variant: "success",
        title: T("inviteDialog.success"),
        description: T("inviteSentTo", { email: inviteEmail.trim() }),
      });
      aggiorna();
      setInviteDialogOpen(false);
      setInviteEmail("");
    },
    onError: errore(T("inviteDialog.failed")),
  });

  // Chi e' gia' entrato si rimuove con il suo id, l'invito in attesa con quello della membership.
  const removeMutation = useMutation({
    mutationFn: async (membro) => {
      const response = await api.delete(`/tenants/${tenantId}/members/${membro.user.id || membro.membership_id}`);
      return response.data;
    },
    onSuccess: (_, membro) => {
      toast({ variant: "success", title: membro.user.id ? T("removeDialog.success") : T("inviteCancelled") });
      aggiorna();
      setDaRimuovere(null);
    },
    onError: errore(T("removeDialog.failed")),
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }) => {
      const response = await api.put(`/tenants/${tenantId}/members/${userId}/role`, { role });
      return response.data;
    },
    onSuccess: () => {
      toast({ variant: "success", title: T("roleUpdated"), description: T("roleUpdatedDesc") });
      aggiorna();
    },
    onError: errore(T("roleUpdateFailed")),
  });

  const handleInvite = (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) {
      toast({ title: T("inviteDialog.emailRequired"), description: T("inviteDialog.emailRequiredDesc"), variant: "destructive" });
      return;
    }
    if (!EMAIL.test(email)) {
      toast({ title: T("inviteDialog.invalidEmail"), description: T("inviteDialog.invalidEmailDesc"), variant: "destructive" });
      return;
    }
    inviteMutation.mutate({ email, role: "admin" });
  };

  const copyInviteLink = async (inviteToken) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/invites/${inviteToken}/accept`);
      toast({ variant: "success", title: T("linkCopied"), description: T("linkCopiedDesc") });
    } catch (error) {
      console.error("Failed to copy invite link:", error);
      toast({ title: T("copyFailed"), description: T("copyFailedDesc"), variant: "destructive" });
    }
  };

  // Il titolare in cima, una volta sola anche se ha pure una membership.
  const proprietario = membri.data?.owner;
  const altri = (membri.data?.members || []).filter(
    (m) => m.role !== "owner" && !(proprietario && m.user?.id && String(m.user.id) === String(proprietario.id))
  );
  const righe = [
    ...(proprietario
      ? [{ membership_id: "owner", user: proprietario, role: "owner", status: "active", createdAt: activeTenant?.createdAt }]
      : []),
    ...altri,
  ];

  const azioniPer = (m) => {
    if (m.role === "owner") return [];
    const attivo = Boolean(m.user?.id);
    const stessa = attivo && io && String(m.user.id) === io;
    const voci = [];
    // I membri di prima di "solo amministratore": il titolare li promuove.
    if (attivo && titolare && !stessa && m.status === "active" && m.role === "member") {
      voci.push({ chiave: "admin", icona: Shield, testo: T("makeAdmin"), fai: () => updateRoleMutation.mutate({ userId: m.user.id, role: "admin" }) });
    }
    if (!attivo && m.invite_token && gestisce) {
      voci.push({ chiave: "copia", icona: Copy, testo: T("copyInviteLink"), fai: () => copyInviteLink(m.invite_token) });
    }
    // Un amministratore non rimuove un altro amministratore: solo il titolare.
    const puoRimuovere = gestisce && !stessa && (titolare || m.role !== "admin");
    if (puoRimuovere) {
      voci.push({
        chiave: "rimuovi",
        icona: Trash2,
        testo: attivo ? T("remove") : T("cancelInvitation"),
        fai: () => setDaRimuovere(m),
        pericolo: true,
      });
    }
    return voci;
  };

  const quandoDi = (m) => {
    if (m.status === "pending") {
      const chi = m.invited_by?.name || m.invited_by?.email;
      const data = giorno(m.invited_at || m.createdAt);
      if (chi && data) return T("invitedByOn", { name: chi, date: data });
      return data ? T("invitedOn", { date: data }) : null;
    }
    const data = giorno(m.createdAt);
    return data ? T("since", { date: data }) : null;
  };

  const invitoDaAnnullare = daRimuovere && !daRimuovere.user.id;

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-[#020617]">
            {T("peopleTitle")}
            {membri.data ? (
              <span className="text-sm font-normal text-[#64748B]">{T("peopleCount", { count: righe.length })}</span>
            ) : null}
          </h2>
          <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">{T("peopleDescription")}</p>
        </div>
        {gestisce ? (
          <button type="button" className={`${SCURO} shrink-0`} onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            {T("inviteCta")}
          </button>
        ) : null}
      </div>

      {membri.isError ? (
        <div role="alert" className="flex items-start gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-800">{T("loadFailed")}</p>
            <p className="mt-1 text-sm text-red-700">
              {membri.error?.response?.data?.message || membri.error?.message}
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white">
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                <TableHead className={TESTATA}>{T("colPerson")}</TableHead>
                <TableHead className={`${TESTATA} hidden sm:table-cell`}>{T("role")}</TableHead>
                <TableHead className={`${TESTATA} hidden sm:table-cell`}>{T("status")}</TableHead>
                <TableHead className={`${TESTATA} hidden md:table-cell`}>{T("colSince")}</TableHead>
                <TableHead className={`${TESTATA} w-12`}>
                  <span className="sr-only">{T("actions")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {membri.isLoading
                ? Array.from({ length: 2 }).map((_, i) => (
                    <TableRow key={i} className="border-[#F1F5F9] hover:bg-transparent">
                      <TableCell className="py-3">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-32" />
                            <Skeleton className="h-3 w-44" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell"><Skeleton className="h-5 w-24 rounded-full" /></TableCell>
                      <TableCell className="hidden sm:table-cell"><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                      <TableCell className="hidden md:table-cell"><Skeleton className="h-3.5 w-28" /></TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                : righe.map((m) => {
                    const ruolo = RUOLI[m.role] || RUOLI.member;
                    const stato = STATI[m.status] || STATI.suspended;
                    const sonoIo = io && m.user?.id && String(m.user.id) === io;
                    const nome = m.user?.name && m.user.name !== m.user.email ? m.user.name : null;
                    const voci = azioniPer(m);
                    return (
                      <TableRow key={m.membership_id} className="border-[#F1F5F9] hover:bg-[#F8FAFC]">
                        <TableCell className="py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold text-[#334155]"
                              aria-hidden="true"
                            >
                              {iniziali(m.user)}
                            </span>
                            <div className="min-w-0 max-w-[150px] sm:max-w-none">
                              <p className="truncate text-sm font-medium text-[#020617]">
                                {nome || m.user?.email}
                                {sonoIo ? <span className="ml-1.5 font-normal text-[#94A3B8]">({T("you")})</span> : null}
                              </p>
                              {nome ? <p className="truncate text-xs text-[#64748B]">{m.user.email}</p> : null}
                              {/* Sul telefono ruolo e stato stanno qui, non in colonne a parte. */}
                              <div className="mt-1 flex flex-wrap gap-1 sm:hidden">
                                <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${ruolo.cls}`}>
                                  {T(ruolo.chiave)}
                                </span>
                                {m.status !== "active" ? (
                                  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${stato.cls}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${stato.dot}`} aria-hidden="true" />
                                    {T(stato.chiave)}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${ruolo.cls}`}>
                            {T(ruolo.chiave)}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${stato.cls}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${stato.dot}`} aria-hidden="true" />
                            {T(stato.chiave)}
                          </span>
                        </TableCell>
                        <TableCell className="hidden text-xs text-[#64748B] md:table-cell">{quandoDi(m) || "—"}</TableCell>
                        <TableCell className="text-right">
                          {voci.length ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={T("actionsFor", { name: nome || m.user?.email })}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  <MoreVertical className="h-4 w-4" aria-hidden="true" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52 rounded-xl">
                                {voci.map(({ chiave, icona: Icona, testo, fai, pericolo }, i) => (
                                  <Fragment key={chiave}>
                                    {pericolo && i > 0 ? <DropdownMenuSeparator /> : null}
                                    <DropdownMenuItem
                                      onClick={fai}
                                      className={pericolo ? "text-red-600 focus:text-red-600" : undefined}
                                    >
                                      <Icona className="mr-2 h-4 w-4" aria-hidden="true" />
                                      {testo}
                                    </DropdownMenuItem>
                                  </Fragment>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              {!membri.isLoading && altri.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="py-4 text-center text-sm text-[#64748B]">
                    {gestisce ? T("onlyYou") : T("noOthers")}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Invitare: l'email, e cosa potra' fare chi entra (sempre da amministratore). */}
      <Dialog
        open={inviteDialogOpen}
        onOpenChange={(aperto) => !inviteMutation.isPending && setInviteDialogOpen(aperto)}
      >
        <DialogContent className="rounded-[14px] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{T("inviteTitle")}</DialogTitle>
            <DialogDescription>{T("inviteDescription")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-5" noValidate>
            <Campo id="invito-email" etichetta={T("inviteDialog.emailLabel")}>
              <input
                id="invito-email"
                type="email"
                autoComplete="off"
                className={CAMPO_TESTO}
                placeholder={T("inviteDialog.emailPlaceholder")}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviteMutation.isPending}
                autoFocus
              />
            </Campo>
            <p className="rounded-[10px] bg-[#F8FAFC] px-3 py-2.5 text-[13px] leading-5 text-[#475569]">
              {T("inviteDialog.roleInfo")}
            </p>
            <DialogFooter className="gap-2 sm:space-x-0">
              <button
                type="button"
                className={GRIGIO}
                onClick={() => setInviteDialogOpen(false)}
                disabled={inviteMutation.isPending}
              >
                {t("common.cancel")}
              </button>
              <button type="submit" className={SCURO} disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {inviteMutation.isPending ? T("inviteDialog.sending") : T("sendInviteCta")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rimuovere una persona o annullare un invito: si conferma qui. */}
      <Dialog
        open={Boolean(daRimuovere)}
        onOpenChange={(aperto) => !aperto && !removeMutation.isPending && setDaRimuovere(null)}
      >
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              {invitoDaAnnullare
                ? T("cancelInviteTitle")
                : T("removeTitle", { name: daRimuovere?.user?.name || daRimuovere?.user?.email })}
            </DialogTitle>
            <DialogDescription>
              {invitoDaAnnullare ? T("cancelInviteText", { email: daRimuovere?.user?.email }) : T("removeText")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:space-x-0">
            <button
              type="button"
              className={GRIGIO}
              onClick={() => setDaRimuovere(null)}
              disabled={removeMutation.isPending}
            >
              {T("back")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              disabled={removeMutation.isPending}
              onClick={() => removeMutation.mutate(daRimuovere)}
            >
              {removeMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {invitoDaAnnullare ? T("cancelInviteCta") : T("removeCta")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
